import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import type { UnidentifiedPatientView } from '@wodi/shared';
import {
  Button,
  Cell,
  Column,
  EmptyState,
  formatShortAge,
  LoadingBlock,
  Notice,
  PageHeader,
  Row,
  Section,
  Table,
  TableBody,
  TableHeader,
  Tag,
  useShortcut,
} from '@wodi/ui';
import { useState } from 'react';

import { unidentifiedPatientsQuery } from '../api/casualty.ts';
import { can, sessionQuery } from '../api/session.ts';
import { queuesQuery } from '../api/triage.ts';
import { formatWaiting } from '../format/waiting.ts';
import { useOnline } from '../hooks/use-online.ts';
import { ShortcutHint, TableScroll } from '../layout/screen.tsx';
import { formatInstant } from '../patients/format.ts';
import styles from './casualty.module.css';
import { errorText, RegisterUnknownDialog } from './register-unknown-dialog.tsx';

// Casualty (docs/plans/phase2-casualty.md §5): register an unknown patient in one step, see
// who waits in the casualty queues, and the patients not yet identified. Addresses carry
// internal IDs only.

const VISIT_WORDS: Record<NonNullable<UnidentifiedPatientView['visit']>['type'], string> = {
  emergency: 'Emergency',
  inpatient: 'Inpatient',
  outpatient: 'Outpatient',
  walk_in: 'Walk-in',
  mortuary: 'Mortuary',
};

function CasualtyQueues() {
  const queues = useQuery(queuesQuery);
  const casualty = (queues.data ?? []).filter((q) => q.kind === 'casualty');
  return (
    <Section title="Casualty queues">
      {queues.isPending ? <LoadingBlock label="Loading queues" rows={2} /> : null}
      {queues.isError ? (
        <Notice
          tone="error"
          action={
            <Button dense onPress={() => void queues.refetch()}>
              Try again
            </Button>
          }
        >
          {errorText(queues.error, 'The queues could not be loaded. Try again.')}
        </Notice>
      ) : null}
      {queues.isSuccess && casualty.length === 0 ? (
        <EmptyState>
          No casualty queue is set up at this facility. An administrator adds one in Settings &gt;
          Service points, with the kind Casualty.
        </EmptyState>
      ) : null}
      <ul className={styles.queues}>
        {casualty.map((q) => (
          <li key={q.id} className={styles.queue}>
            <Link to="/queues" search={{ servicePointId: q.id }} className={styles.queueName}>
              {q.name}
            </Link>
            <span className={styles.meta}>
              {q.waiting === 0
                ? 'No one waiting'
                : `${String(q.waiting)} waiting${
                    q.longestWaitMinutes === null
                      ? ''
                      : `, longest ${formatWaiting(q.longestWaitMinutes)}`
                  }`}
            </span>
          </li>
        ))}
      </ul>
    </Section>
  );
}

function Unidentified({ mayIdentify }: { mayIdentify: boolean }) {
  const list = useQuery(unidentifiedPatientsQuery);
  return (
    <Section title="Unidentified patients">
      {list.isPending ? <LoadingBlock label="Loading unidentified patients" rows={3} /> : null}
      {list.isError ? (
        <Notice
          tone="error"
          action={
            <Button dense onPress={() => void list.refetch()}>
              Try again
            </Button>
          }
        >
          {errorText(list.error, 'The list could not be loaded. Try again.')}
        </Notice>
      ) : null}
      {list.isSuccess && list.data.items.length === 0 ? (
        <EmptyState>No unidentified patients. Everyone here has a name.</EmptyState>
      ) : null}
      {list.isSuccess && list.data.items.length > 0 ? (
        <TableScroll>
          <Table aria-label="Unidentified patients" dense>
            <TableHeader>
              <Column isRowHeader>Temporary label</Column>
              <Column>Sex and age</Column>
              <Column>Visit</Column>
              <Column>Where now</Column>
              <Column>Registered</Column>
              <Column>Action</Column>
            </TableHeader>
            <TableBody items={list.data.items}>
              {(p) => (
                <Row id={p.patientId}>
                  <Cell>
                    <Link
                      to="/patients/$patientId"
                      params={{ patientId: p.patientId }}
                      className={styles.label}
                    >
                      {p.temporaryLabel}
                    </Link>
                  </Cell>
                  <Cell>{`${p.sex}, ${formatShortAge(p.age, p.ageEstimated)}`}</Cell>
                  <Cell>{p.visit === null ? 'No open visit' : VISIT_WORDS[p.visit.type]}</Cell>
                  <Cell>{p.visit?.location ?? 'Not in a queue'}</Cell>
                  <Cell>{formatInstant(p.registeredAt)}</Cell>
                  <Cell>
                    {p.mergeRequested ? (
                      <Tag state="pending">Merge waiting</Tag>
                    ) : mayIdentify ? (
                      <Link to="/patients/$patientId/identify" params={{ patientId: p.patientId }}>
                        Identify patient
                      </Link>
                    ) : (
                      <span className={styles.meta}>Not identified</span>
                    )}
                  </Cell>
                </Row>
              )}
            </TableBody>
          </Table>
        </TableScroll>
      ) : null}
    </Section>
  );
}

export function CasualtyPage() {
  const { data: session } = useSuspenseQuery(sessionQuery);
  const queues = useQuery(queuesQuery);
  const online = useOnline();
  const navigate = useNavigate();
  const [registering, setRegistering] = useState(false);
  const mayRegister = session !== null && can(session, 'casualty.register');
  const mayIdentify = session !== null && can(session, 'patients.merge.request');
  const casualtyPoints = (queues.data ?? []).filter((q) => q.kind === 'casualty');
  useShortcut(
    'u',
    () => {
      if (mayRegister && online) setRegistering(true);
    },
    { enabled: mayRegister, description: 'Register an unknown patient' },
  );

  if (session === null || !can(session, 'patients.read')) {
    return (
      <>
        <PageHeader title="Casualty" />
        <Notice tone="info">
          You don't have permission to see casualty. Ask an administrator if you need it.
        </Notice>
      </>
    );
  }
  return (
    <div className={styles.page}>
      <PageHeader
        title="Casualty"
        description="Register a patient nobody can name yet; merge their record once they are identified."
        actions={
          mayRegister ? (
            <Button
              variant="primary"
              isDisabled={!online}
              onPress={() => {
                setRegistering(true);
              }}
            >
              Register unknown patient
            </Button>
          ) : null
        }
      />
      {mayRegister ? <ShortcutHint combo="u" action="register an unknown patient" /> : null}
      {!online ? (
        <Notice tone="info">
          Casualty needs a connection to Wodi. Reconnect to register patients and see the queue.
        </Notice>
      ) : null}
      <CasualtyQueues />
      <Unidentified mayIdentify={mayIdentify} />
      <RegisterUnknownDialog
        isOpen={registering}
        onOpenChange={setRegistering}
        points={casualtyPoints}
        onRegistered={(result) => {
          setRegistering(false);
          void navigate({
            to: '/queues',
            search: { servicePointId: result.servicePointId, entryId: result.queueEntryId },
          });
        }}
      />
    </div>
  );
}
